import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { JwtPayload } from '@sime/shared';
import { useAmbitoStore } from './ambito.store';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: JwtPayload | null;
  isAuthenticated: boolean;
  setAuth: (token: string, refreshToken: string, user: JwtPayload) => void;
  logout: () => void;
}

const UNAUTHENTICATED_STATE = {
  token: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
} as const;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...UNAUTHENTICATED_STATE,
      setAuth: (token, refreshToken, user) => {
        set({ token, refreshToken, user, isAuthenticated: true });
        // If a previously persisted ambito doesn't belong to this user, clear it.
        // Prevents the RSO-leak scenario where one user's selection bled into another's session.
        // Wrapped in try/catch + sessionStorage fallback because if reset() ever throws
        // (corrupt persist data, storage quota), the leak the code is meant to prevent
        // would silently come back. Direct sessionStorage removal is the last line of defense.
        try {
          const currentAmbito = useAmbitoStore.getState().selectedAmbitoId;
          if (currentAmbito !== null && !user.ambitoIds.includes(currentAmbito)) {
            useAmbitoStore.getState().reset();
          }
        } catch (err) {
          console.error('[auth.store] failed to reset stale ambito on setAuth', err);
          try { sessionStorage.removeItem('sime-ambito'); } catch { /* swallow */ }
        }
      },
      logout: () => {
        set(UNAUTHENTICATED_STATE);
        try {
          useAmbitoStore.getState().reset();
        } catch {
          try { sessionStorage.removeItem('sime-ambito'); } catch { /* swallow */ }
        }
      },
    }),
    {
      name: 'sime-auth',
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Clear corrupted state: if marked authenticated but missing required fields
        if (state.isAuthenticated && (!state.token || !state.user)) {
          state.token = null;
          state.refreshToken = null;
          state.user = null;
          state.isAuthenticated = false;
        }
      },
    },
  ),
);
